import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Clock, ExternalLink, Globe, Mail, MapPin, Phone, Users } from 'lucide-react';
import { PageSEO } from '../../components/seo/SchemaMarkup';
import { API, Eyebrow, LocatorLoading, SEGMENT_LABELS, therapyLabel, trackClick, useJsonLd, useLocatorPageView } from './locatorShared';

const ExtLink = ({ href, onClick, className, children, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className} data-testid={testId}>{children}</a>
);

const PriceRow = ({ row, slug }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap py-2.5 border-b border-zinc-800/80 last:border-0" data-testid={`price-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}>
    <div className="flex-1 min-w-[180px]">
      <p className="text-white text-sm">{row.label}</p>
      {row.note && <p className="text-zinc-500 text-xs mt-0.5">{row.note}</p>}
    </div>
    <span className={`text-sm font-bold ${row.price ? 'text-[#C7FA3E]' : 'text-zinc-500 font-normal italic'}`}>
      {row.price || 'Pricing pending'}
    </span>
    {row.cta_url && (
      <ExtLink
        href={row.cta_url}
        onClick={() => trackClick(slug, 'profile', 'cta', row.cta_url, row.cta_label)}
        className="text-xs border border-zinc-700 text-zinc-200 px-3 py-1.5 hover:border-[#C7FA3E] hover:text-[#C7FA3E] transition-colors whitespace-nowrap"
      >
        {row.cta_label}
      </ExtLink>
    )}
  </div>
);

export default function LocatorFacilityProfile() {
  const { slug } = useParams();
  useLocatorPageView(slug);
  const [f, setF] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setF(null);
    setNotFound(false);
    fetch(`${API}/api/locator/facility/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => ((d.profile ? setF(d) : setNotFound(true))))
      .catch(() => setNotFound(true));
  }, [slug]);

  const p = f?.profile;
  useJsonLd('locator-facility-profile', f ? {
    '@context': 'https://schema.org',
    '@type': p.schema_type || 'LocalBusiness',
    name: f.name,
    url: `https://ninjathlete.com/locator/facility/${f.slug}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: f.street_address,
      addressLocality: f.city,
      addressRegion: f.state,
      ...(p.zip ? { postalCode: p.zip } : {}),
      addressCountry: 'US',
    },
    ...(f.phone ? { telephone: f.phone } : {}),
    ...(p.email_public ? { email: p.email_public } : {}),
    ...(f.website ? { sameAs: [f.website] } : {}),
    ...(p.hours ? { openingHours: p.hours } : {}),
    makesOffer: (p.schema_offers || []).map(([name, price]) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name },
      price,
      priceCurrency: 'USD',
    })),
  } : null);

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-white text-3xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>No Profile Here Yet</h1>
          <Link to="/locator" className="text-[#C7FA3E] text-sm underline underline-offset-2">Browse the recovery locator</Link>
        </div>
      </div>
    );
  }
  if (!f) return <LocatorLoading />;

  const claimed = f.tier === 'claimed';

  return (
    <article className="bg-black min-h-screen px-6 pt-28 pb-16" data-testid={`facility-profile-${slug}`}>
      <PageSEO
        title={p.seo_title || `${f.name} | NinjAthlete Recovery Locator`}
        description={p.seo_description || f.description}
        path={`/locator/facility/${slug}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Recovery Locator', url: '/locator' },
          ...(f.page_slug ? [{ name: f.state, url: `/locator/${f.page_slug}` }] : []),
          { name: f.name, url: `/locator/facility/${slug}` },
        ]}
      />
      <div className="max-w-4xl mx-auto">
        <Eyebrow>
          <Link to="/locator" className="hover:text-[#C7FA3E]">Recovery Locator</Link>
          {f.page_slug && <> · <Link to={`/locator/${f.page_slug}`} className="hover:text-[#C7FA3E]">{f.state}</Link></>}
          {' '}· {SEGMENT_LABELS[f.segment] || 'Facility'}
        </Eyebrow>

        <div className="flex items-start gap-3 flex-wrap">
          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="facility-profile-h1">
            {f.name}
          </h1>
          {claimed && (
            <span className="inline-flex items-center gap-1 bg-[#C7FA3E]/10 border border-[#C7FA3E]/40 text-[#C7FA3E] text-[11px] uppercase tracking-wider px-2.5 py-1 mt-3" data-testid="facility-verified-badge">
              <BadgeCheck className="w-3.5 h-3.5" /> Verified by the business
            </span>
          )}
        </div>

        <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-3xl">{p.about || f.description}</p>

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-6 text-sm">
          <p className="text-zinc-300 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" /> {[f.street_address, f.city, f.state].filter(Boolean).join(', ')}{p.zip ? ` ${p.zip}` : ''}</p>
          {f.phone && <a href={`tel:${f.phone}`} onClick={() => trackClick(slug, 'profile', 'phone', f.phone, f.name)} className="text-zinc-300 hover:text-[#C7FA3E] flex items-center gap-2" data-testid="profile-phone"><Phone className="w-4 h-4 text-zinc-500 shrink-0" /> {f.phone}</a>}
          {p.email_public && <a href={`mailto:${p.email_public}`} className="text-zinc-300 hover:text-[#C7FA3E] flex items-center gap-2" data-testid="profile-email"><Mail className="w-4 h-4 text-zinc-500 shrink-0" /> {p.email_public}</a>}
          {f.website && <ExtLink href={f.website} onClick={() => trackClick(slug, 'profile', 'website', f.website, f.name)} className="text-zinc-300 hover:text-[#C7FA3E] flex items-center gap-2" testId="profile-website"><Globe className="w-4 h-4 text-zinc-500 shrink-0" /> {f.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</ExtLink>}
        </div>

        {(p.hours || p.session_note) && (
          <div className="mt-5 border border-zinc-800 bg-zinc-900/50 p-4 inline-block" data-testid="profile-hours">
            <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] mb-1.5 flex items-center gap-1.5"><Clock className="w-3 h-3" /> Hours</p>
            {(p.hours || []).map((h) => <p key={h} className="text-zinc-300 text-sm">{h}</p>)}
            {p.session_note && <p className="text-zinc-500 text-xs mt-1.5">{p.session_note}</p>}
          </div>
        )}

        {p.primary_cta && (
          <div className="mt-6">
            <ExtLink
              href={p.primary_cta.url}
              onClick={() => trackClick(slug, 'profile', 'cta', p.primary_cta.url, p.primary_cta.label)}
              className="inline-flex items-center gap-2 bg-[#C7FA3E] text-black text-sm font-bold uppercase tracking-wider px-6 py-3.5 hover:bg-[#d4ff5c] transition-colors"
              testId="profile-primary-cta"
            >
              {p.primary_cta.label} <ArrowRight className="w-4 h-4" />
            </ExtLink>
          </div>
        )}

        {p.audience && (
          <p className="text-zinc-400 text-sm mt-6 flex items-start gap-2 max-w-3xl" data-testid="profile-audience">
            <Users className="w-4 h-4 mt-0.5 text-zinc-500 shrink-0" /> {p.audience}
          </p>
        )}

        {p.services?.length > 0 && (
          <section className="mt-10" data-testid="profile-services">
            <h2 className="text-white text-2xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Services &amp; Equipment</h2>
            <div className="flex flex-wrap gap-2">
              {p.services.map((s) => (
                <span key={s} className="text-zinc-300 text-xs border border-zinc-800 bg-zinc-900/50 px-3 py-1.5">{s}</span>
              ))}
            </div>
            {f.therapies?.length > 0 && (
              <p className="text-zinc-500 text-xs mt-3">
                Listed under: {f.therapies.map((t) => therapyLabel(t)).join(' · ')}
              </p>
            )}
          </section>
        )}

        {(p.pricing_groups || []).map((g) => (
          <section key={g.title} className="mt-10" data-testid={`pricing-group-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}>
            <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{g.title}</h2>
            {g.note && <p className="text-zinc-500 text-xs mt-1 mb-2">{g.note}</p>}
            <div className="border border-zinc-800 bg-zinc-900/50 px-4 py-1.5 mt-3">
              {g.rows.map((row) => <PriceRow key={row.label} row={row} slug={slug} />)}
            </div>
            {g.cta && (
              <ExtLink
                href={g.cta.url}
                onClick={() => trackClick(slug, 'profile', 'cta', g.cta.url, g.cta.label)}
                className="inline-flex items-center gap-2 mt-3 border border-[#C7FA3E]/60 text-[#C7FA3E] text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-[#C7FA3E] hover:text-black transition-colors"
              >
                {g.cta.label} <ExternalLink className="w-3.5 h-3.5" />
              </ExtLink>
            )}
          </section>
        ))}

        {p.extra_ctas?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-3" data-testid="profile-extra-ctas">
            {p.extra_ctas.map((c) => (
              <ExtLink
                key={c.label}
                href={c.url}
                onClick={() => trackClick(slug, 'profile', 'cta', c.url, c.label)}
                className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors ${c.secondary ? 'border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200' : 'border border-[#C7FA3E]/60 text-[#C7FA3E] hover:bg-[#C7FA3E] hover:text-black'}`}
              >
                {c.label} <ExternalLink className="w-3.5 h-3.5" />
              </ExtLink>
            ))}
          </div>
        )}

        {p.related && (
          <section className="mt-12" data-testid="profile-related">
            <h2 className="text-white text-2xl mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Related Facility</h2>
            <Link to={`/locator/facility/${p.related.slug}`} className="block border border-zinc-800 bg-zinc-900/50 p-5 hover:border-[#C7FA3E]/50 transition-colors group max-w-xl">
              <h3 className="text-white text-sm font-bold group-hover:text-[#C7FA3E] transition-colors">{p.related.name}</h3>
              <p className="text-zinc-500 text-xs mt-1.5 leading-relaxed">{p.related.note}</p>
              <span className="inline-block text-[#C7FA3E] text-[11px] uppercase tracking-wider mt-3">View profile →</span>
            </Link>
          </section>
        )}

        <p className="text-zinc-600 text-xs mt-12 max-w-3xl">
          Details confirmed by the business or verified on its official public booking pages. Pricing marked "pending" has not been supplied — contact the facility directly. Equipment listings describe available services and are not medical claims.
        </p>
        <p className="text-zinc-500 text-sm mt-4">
          {f.page_slug && <>Back to the <Link to={`/locator/${f.page_slug}`} className="text-[#C7FA3E] hover:underline">{f.state} recovery directory</Link> or </>}
          browse the <Link to="/locator" className="text-[#C7FA3E] hover:underline">national recovery locator</Link>.
        </p>
      </div>
    </article>
  );
}
